import React from "react";
import { Helmet } from "react-helmet-async";

export default function AlmaFleurSA() {
	const titlePage =
		"Alma Fleur SA | Boutique en ligne fleuriste haut de gamme | Weblax Studio";
	const description =
		"Weblax a conçu et développé la boutique en ligne d'Alma Fleur SA : e-commerce floral sur mesure, stratégie SEO locale, UX optimisée et +65% de trafic organique. Découvrez ce projet réalisé par notre agence web.";
	const url = "https://weblax.fr/realisations/alma-fleur-sa";

	const stats = [
		{ value: "+65%", label: "Trafic organique", icon: "📈" },
		{ value: "+42%", label: "Taux de conversion", icon: "🎯" },
		{ value: "-30%", label: "Taux de rebond mobile", icon: "📱" },
		{ value: "×2", label: "Rapidité catalogue", icon: "⚡" },
	];

	const features = [
		{
			icon: "🛒",
			title: "Catalogue produit structuré",
			desc: "Architecture produit pensée pour la navigation intuitive et le référencement : bouquets, plantes, compositions événementielles organisés par catégories florales optimisées SEO.",
		},
		{
			icon: "🎨",
			title: "Design émotionnel sur mesure",
			desc: "Direction artistique centrée sur l'univers sensoriel du floral : typographies élégantes, palette douce, photographies mises en scène pour susciter l'envie d'achat.",
		},
		{
			icon: "📍",
			title: "SEO local avancé",
			desc: "Stratégie de référencement local complète : balisage schema.org, fiches produits optimisées, maillage interne et ciblage des requêtes transactionnelles à fort potentiel.",
		},
		{
			icon: "📲",
			title: "Mobile-first & performance",
			desc: "Interface responsive conçue en priorité pour mobile. Optimisation Core Web Vitals, lazy loading et compression d'images pour une expérience fluide sur tous appareils.",
		},
		{
			icon: "✨",
			title: "Personnalisation produit",
			desc: "Gestion des variantes : taille du bouquet, occasion, message personnalisé, option livraison. Parcours d'achat simplifié pour maximiser le taux de transformation.",
		},
		{
			icon: "📊",
			title: "Analytics & conversion",
			desc: "Mise en place d'un tracking avancé, heat maps, et optimisation A/B des pages produits pour piloter la performance commerciale avec précision.",
		},
	];

	const timeline = [
		{
			week: "Semaine 1",
			title: "Cadrage stratégique",
			desc: "Audit UX du site existant, analyse de la concurrence locale, identification des opportunités SEO et définition des KPIs.",
		},
		{
			week: "Semaine 2",
			title: "Architecture & wireframes",
			desc: "Structuration de l'arborescence e-commerce, maquettes fonctionnelles et validation du parcours utilisateur.",
		},
		{
			week: "Semaine 3",
			title: "Direction artistique",
			desc: "Création de l'identité visuelle digitale, charte graphique, sélection typographique et direction photo.",
		},
		{
			week: "Semaine 4",
			title: "Développement React",
			desc: "Intégration front-end complète, développement des composants, connexion API et optimisation des performances.",
		},
		{
			week: "Semaine 5",
			title: "SEO & optimisation",
			desc: "Rédaction des contenus optimisés, balisage sémantique, schema.org, optimisation technique et speed tests.",
		},
		{
			week: "Semaine 6",
			title: "Mise en ligne",
			desc: "Déploiement, tests multi-devices, ajustements finaux et formation client à la gestion du catalogue.",
		},
	];

	const techStack = [
		{ name: "React", desc: "Interface utilisateur" },
		{ name: "Next.js", desc: "SSR & performances" },
		{ name: "Tailwind CSS", desc: "Design system" },
		{ name: "Schema.org", desc: "SEO structuré" },
		{ name: "Vercel", desc: "Déploiement edge" },
		{ name: "Core Web Vitals", desc: "Performance" },
	];

	return (
		<>
			<Helmet>
				<title>{titlePage}</title>
				<meta name="description" content={description} />
				<link rel="canonical" href={url} />
				<meta property="og:type" content="website" />
				<meta property="og:title" content={titlePage} />
				<meta property="og:description" content={description} />
				<meta property="og:url" content={url} />
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "CreativeWork",
						name: "Alma Fleur SA - Boutique e-commerce florale",
						description,
						url,
						dateCreated: "2024",
						provider: {
							"@type": "Organization",
							name: "Weblax",
							url: "https://weblax.fr",
						},
						result: {
							"@type": "WebSite",
							url: "https://almafleur.ch",
							name: "Alma Fleur SA",
						},
					})}
				</script>
			</Helmet>

			{/* ── Hero ── */}
			<section className="relative overflow-hidden bg-gradient-to-br from-rose-50 via-white to-green-50 border-b border-border">
				<div
					className="absolute inset-0 opacity-30"
					style={{
						backgroundImage:
							"radial-gradient(circle at 20% 50%, #fce7f3 0%, transparent 50%), radial-gradient(circle at 80% 20%, #dcfce7 0%, transparent 50%)",
					}}
				/>
				<div className="weblax-container py-20 relative">
					<div className="flex flex-wrap items-center gap-3 mb-6">
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-rose-100 text-rose-700 border border-rose-200">
							🌸 E-commerce floral
						</span>
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-green-100 text-green-700 border border-green-200">
							✅ Projet livré
						</span>
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-blue-100 text-blue-700 border border-blue-200">
							🇨🇭 Suisse
						</span>
					</div>

					<h1 className="weblax-heading-display mb-6 max-w-4xl">
						Boutique en ligne haut de gamme pour{" "}
						<span className="text-rose-600">Alma Fleur SA</span>
					</h1>

					<p className="text-lg text-muted-foreground max-w-3xl leading-relaxed mb-10">
						Weblax a conçu et développé de A à Z la plateforme e-commerce d'Alma
						Fleur SA — un fleuriste artisan suisse spécialisé dans les
						compositions florales haut de gamme. Un projet alliant{" "}
						<strong>performance technique</strong>,{" "}
						<strong>SEO local avancé</strong> et
						<strong> expérience utilisateur émotionnelle</strong> pour
						transformer les visiteurs en acheteurs.
					</p>

					{/* Stats */}
					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						{stats.map((s) => (
							<div
								key={s.label}
								className="bg-white/80 backdrop-blur border border-border rounded-xl p-5 text-center shadow-sm"
							>
								<div className="text-2xl mb-1">{s.icon}</div>
								<div className="text-3xl font-bold text-foreground mb-1">
									{s.value}
								</div>
								<div className="text-xs text-muted-foreground">{s.label}</div>
							</div>
						))}
					</div>
				</div>
			</section>

			<main className="weblax-container py-20 space-y-24">
				{/* ── Contexte ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-rose-400" />
						<span className="text-xs font-semibold text-rose-600 uppercase tracking-widest">
							Contexte
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-6">
						Un fleuriste artisan face à la transformation digitale
					</h2>
					<div className="grid md:grid-cols-2 gap-8">
						<div className="space-y-4 text-muted-foreground leading-relaxed">
							<p>
								Alma Fleur SA est un fleuriste artisan basé en Suisse romande,
								reconnu pour la qualité de ses compositions et son sens du
								détail. Malgré une clientèle fidèle en boutique physique,
								l'entreprise peinait à capter une audience digitale en pleine
								croissance.
							</p>
							<p>
								Le site existant souffrait d'une architecture obsolète, d'une
								expérience mobile défaillante et d'une visibilité quasi nulle
								sur les moteurs de recherche. Des opportunités de vente se
								perdaient chaque jour sur des requêtes comme{" "}
								<em>"fleuriste livraison Genève"</em>,
								<em>"bouquet anniversaire en ligne"</em> ou{" "}
								<em>"composition florale mariage Suisse"</em>.
							</p>
						</div>
						<div className="space-y-3">
							{[
								{
									label: "Trafic organique initial",
									value: "Très faible",
									color: "text-red-500",
								},
								{
									label: "Position SEO locale",
									value: "Hors top 20",
									color: "text-red-500",
								},
								{
									label: "Taux de conversion mobile",
									value: "<1%",
									color: "text-orange-500",
								},
								{
									label: "Score performance (PageSpeed)",
									value: "38/100",
									color: "text-orange-500",
								},
							].map((item) => (
								<div
									key={item.label}
									className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted/30"
								>
									<span className="text-sm text-muted-foreground">
										{item.label}
									</span>
									<span className={`text-sm font-semibold ${item.color}`}>
										{item.value}
									</span>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* ── Demandes client ── */}
				<section className="bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-100 rounded-2xl p-10">
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-rose-400" />
						<span className="text-xs font-semibold text-rose-600 uppercase tracking-widest">
							Brief client
						</span>
					</div>
					<h2 className="text-2xl font-bold mb-8">
						Ce qu'Alma Fleur SA attendait de nous
					</h2>
					<div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
						{[
							{
								icon: "🌹",
								text: "Une boutique en ligne qui reflète l'élégance de la marque",
							},
							{
								icon: "📦",
								text: "Un catalogue produit structuré et évolutif",
							},
							{
								icon: "📍",
								text: "Une meilleure visibilité locale sur Google",
							},
							{ icon: "⚡", text: "Un processus d'achat rapide et intuitif" },
							{ icon: "📱", text: "Une expérience mobile irréprochable" },
							{
								icon: "🎁",
								text: "La personnalisation des commandes (occasion, message, taille)",
							},
						].map((item) => (
							<div
								key={item.text}
								className="flex items-start gap-3 bg-white rounded-xl p-4 border border-rose-100 shadow-sm"
							>
								<span className="text-xl shrink-0">{item.icon}</span>
								<span className="text-sm text-muted-foreground leading-relaxed">
									{item.text}
								</span>
							</div>
						))}
					</div>
				</section>

				{/* ── Notre approche ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-rose-400" />
						<span className="text-xs font-semibold text-rose-600 uppercase tracking-widest">
							Méthodologie
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-4">
						Notre approche : émotion + performance
					</h2>
					<p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
						Créer une boutique florale en ligne, c'est bien plus que développer
						un site e-commerce. C'est concevoir une expérience sensorielle
						digitale où chaque détail visuel, chaque animation et chaque mot
						doit évoquer la beauté et l'émotion associées aux fleurs — tout en
						restant performant et orienté conversion.
					</p>
					<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
						{features.map((f) => (
							<div
								key={f.title}
								className="group p-6 rounded-2xl border border-border hover:border-rose-200 hover:shadow-md transition-all duration-200 bg-white"
							>
								<span className="text-3xl mb-4 block">{f.icon}</span>
								<h3 className="font-semibold text-foreground mb-2">
									{f.title}
								</h3>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{f.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Timeline ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-rose-400" />
						<span className="text-xs font-semibold text-rose-600 uppercase tracking-widest">
							Déroulement
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-10">
						6 semaines, de l'idée au lancement
					</h2>
					<div className="relative">
						<div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-rose-200 via-rose-300 to-transparent hidden md:block" />
						<div className="space-y-6">
							{timeline.map((item, i) => (
								<div key={item.week} className="relative flex gap-6 group">
									<div className="shrink-0 w-12 h-12 rounded-full bg-rose-100 border-2 border-rose-200 flex items-center justify-center text-rose-600 font-bold text-sm z-10">
										{i + 1}
									</div>
									<div className="flex-1 pb-6">
										<div className="flex flex-wrap items-center gap-3 mb-1">
											<span className="text-xs font-medium text-rose-500">
												{item.week}
											</span>
											<h3 className="font-semibold text-foreground">
												{item.title}
											</h3>
										</div>
										<p className="text-sm text-muted-foreground leading-relaxed">
											{item.desc}
										</p>
									</div>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* ── Stack technique ── */}
				<section className="bg-muted/30 rounded-2xl border border-border p-10">
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-rose-400" />
						<span className="text-xs font-semibold text-rose-600 uppercase tracking-widest">
							Stack technique
						</span>
					</div>
					<h2 className="text-2xl font-bold mb-8">Technologies mobilisées</h2>
					<div className="flex flex-wrap gap-3">
						{techStack.map((tech) => (
							<div
								key={tech.name}
								className="flex items-center gap-2 bg-white border border-border rounded-lg px-4 py-2.5 shadow-sm"
							>
								<span className="font-semibold text-sm text-foreground">
									{tech.name}
								</span>
								<span className="text-xs text-muted-foreground">
									— {tech.desc}
								</span>
							</div>
						))}
					</div>
				</section>

				{/* ── Résultats ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-rose-400" />
						<span className="text-xs font-semibold text-rose-600 uppercase tracking-widest">
							Impact
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-4">
						Des résultats mesurables dès les premiers mois
					</h2>
					<p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
						Trois mois après le lancement de la nouvelle plateforme, les
						indicateurs de performance démontrent l'efficacité de notre approche
						combinée design + SEO + UX.
					</p>
					<div className="grid md:grid-cols-2 gap-6">
						{[
							{
								metric: "+65%",
								detail: "de trafic organique en 3 mois",
								desc: "Grâce à une stratégie SEO locale ciblée et une architecture technique optimisée pour le crawl Google.",
								color: "from-green-50 to-emerald-50",
								accent: "text-green-600",
								border: "border-green-100",
							},
							{
								metric: "+42%",
								detail: "de taux de conversion",
								desc: "Résultat d'un parcours d'achat repensé, d'une présentation produit émotionnelle et d'un tunnel de commande simplifié.",
								color: "from-blue-50 to-cyan-50",
								accent: "text-blue-600",
								border: "border-blue-100",
							},
							{
								metric: "-30%",
								detail: "de taux de rebond mobile",
								desc: "Grâce à une interface mobile-first fluide, des temps de chargement réduits et une navigation intuitive.",
								color: "from-purple-50 to-violet-50",
								accent: "text-purple-600",
								border: "border-purple-100",
							},
							{
								metric: "×2",
								detail: "plus rapide sur le catalogue",
								desc: "Optimisation du rendu, pagination intelligente et mise en cache avancée pour une navigation instantanée.",
								color: "from-amber-50 to-orange-50",
								accent: "text-amber-600",
								border: "border-amber-100",
							},
						].map((r) => (
							<div
								key={r.metric}
								className={`bg-gradient-to-br ${r.color} border ${r.border} rounded-2xl p-8`}
							>
								<div className={`text-5xl font-bold mb-2 ${r.accent}`}>
									{r.metric}
								</div>
								<div className="font-semibold text-foreground mb-3">
									{r.detail}
								</div>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{r.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Témoignage / trust ── */}
				<section className="border border-rose-100 bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-10 text-center">
					<span className="text-4xl mb-6 block">🌸</span>
					<blockquote className="text-xl font-medium text-foreground max-w-2xl mx-auto mb-6 leading-relaxed">
						"Weblax a parfaitement compris notre univers. La boutique reflète
						l'âme d'Alma Fleur — élégante, intuitive, et les résultats
						commerciaux parlent d'eux-mêmes."
					</blockquote>
					<cite className="text-sm text-muted-foreground not-italic">
						Direction Alma Fleur SA — Suisse romande
					</cite>
				</section>

				{/* ── Conclusion + CTA ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-rose-400" />
						<span className="text-xs font-semibold text-rose-600 uppercase tracking-widest">
							Conclusion
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-6">
						Un projet qui prouve la puissance du digital pour le commerce local
					</h2>
					<div className="max-w-3xl space-y-4 text-muted-foreground leading-relaxed mb-12">
						<p>
							Le projet Alma Fleur SA démontre qu'une approche rigoureuse
							combinant design émotionnel, architecture technique solide et
							stratégie SEO locale peut transformer durablement la performance
							commerciale d'un artisan fleuriste.
						</p>
						<p>
							Au-delà des chiffres, ce projet a permis à Alma Fleur SA de
							s'imposer comme une référence digitale dans son secteur, en
							proposant une expérience d'achat à la hauteur de la qualité de ses
							créations florales.
						</p>
						<p>
							Chez Weblax, chaque projet est une opportunité de créer quelque
							chose d'unique : une plateforme qui ressemble à notre client, qui
							parle à ses clients, et qui performe durablement dans les
							résultats de recherche.
						</p>
					</div>

					{/* CTA */}
					<div className="bg-foreground text-background rounded-2xl p-10 flex flex-col md:flex-row items-center justify-between gap-6">
						<div>
							<h3 className="text-xl font-bold mb-2">
								Votre projet mérite le même niveau d'attention
							</h3>
							<p className="text-sm opacity-70">
								Parlons de votre boutique en ligne, votre SEO ou votre refonte
								digitale.
							</p>
						</div>
						<a
							href="/contact"
							className="shrink-0 bg-white text-foreground font-semibold px-8 py-3 rounded-xl hover:bg-rose-50 transition-colors text-sm whitespace-nowrap"
						>
							Démarrer un projet →
						</a>
					</div>
				</section>
			</main>
		</>
	);
}
